import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Home, Scale, CreditCard, FileText, Calculator, ExternalLink, Shield } from 'lucide-react';
import { partners } from '../mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const iconMap = {
  'Mortgage Services': Building2,
  'Realtor Partner': Home,
  'Legal Partner': Scale,
  'Debt Management': CreditCard,
  'Tax & Accounting': Calculator,
  'Home Insurance': Shield
};

const PartnersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">Our Partners</h1>
          <p className="text-lg text-blue-100">
            Collaborating with industry leaders to serve you better
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Working Together for Your Success</h2>
          <p className="text-gray-600 leading-relaxed text-lg">
            At Utmost Financial Inc., we believe in the power of collaboration. We've established strong partnerships with trusted industry leaders across various sectors to provide you with comprehensive financial solutions. From real estate professionals to legal experts, our network of partners ensures you have access to the best services available.
          </p>
          <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-6"></div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partners.map((partner) => {
              const Icon = iconMap[partner.category] || Building2;
              return (
                <Card key={partner.id} className="bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-[#0077B6]" />
                    </div>
                    <div className="text-sm font-bold text-[#D4AF37] mb-1">{partner.category}</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{partner.name}</h3>
                    <p className="text-gray-600 text-base mb-4">{partner.description}</p>
                    {partner.link && (
                      partner.isInternal ? (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="border-2 border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white font-bold"
                        >
                          <Link to={partner.link}>
                            Visit Website <ExternalLink className="ml-2 h-3 w-3" />
                          </Link>
                        </Button>
                      ) : (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="border-2 border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white font-bold"
                        >
                          <a href={partner.link} target="_blank" rel="noopener noreferrer">
                            Visit Website <ExternalLink className="ml-2 h-3 w-3" />
                          </a>
                        </Button>
                      )
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* DLC Highlight */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Card className="bg-[#F0F7FF] border-2 border-[#D4AF37]">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-4">
                  <Building2 className="h-8 w-8 text-[#0077B6]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Dominion Lending Centres Partnership</h2>
                <p className="text-gray-600 text-lg mb-4">
                  Our mortgage services are proudly offered through <strong className="text-[#0077B6]">Dominion Lending Centres Utmost Financial</strong>, one of Canada's leading mortgage brokerages. This partnership allows us to provide you with access to competitive rates from multiple lenders and expert mortgage advice.
                </p>
                <Button
                  asChild
                  size="lg"
                  className="bg-[#0077B6] hover:bg-[#005A8C] text-white font-bold"
                >
                  <a href="https://www.dlcutmostfinancial.ca" target="_blank" rel="noopener noreferrer">
                    Visit DLC Utmost Financial
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need a Referral?</h2>
          <p className="text-blue-100 text-lg mb-6">
            Looking for a trusted professional in real estate, insurance, or other financial services? We're happy to connect you with our partners.
          </p>
          <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold text-lg px-8">
            <Link to="/team">Contact Our Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
